import type { Carer, HomeDto } from '@lmc/ts-common'
import { UserRole } from '@lmc/ts-common'
import { Button, Form } from '@lmc/ui-kit'
import { Heading } from '@lmc/ui-kit/src/general'
import { Form as AntdForm, Modal } from 'antd'
import { showToast } from 'common/utils'
import dayjs from 'dayjs'
import useFeatureFlags from 'hooks/useFeatureFlags'
import { type FC, useEffect, useRef, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { useSelector } from 'react-redux'
import { ButtonsContainer } from 'screens/Rostering/components/ButtonsContainer'
import { useShiftTypesList } from 'screens/Rostering/hooks/useShiftTypesList'
import useUsersList from 'screens/Rostering/hooks/useUsersList'
import { useVisitTypesList } from 'screens/Rostering/hooks/useVisitTypesList'
import { styled } from 'styled-components'
import { commonPatterns } from 'utils/enhancedTestIds'
import type { InitialState } from '../../components/ModalStepperForm'
import { VISIT } from '../../constants'
import useGenerate from '../../hooks/useGenerate'
import useInstancesCreate from '../../hooks/useInstancesCreate'
import useRotaGenerated, {
  type GeneratedRota
} from '../../hooks/useRotaGenerated'
import { useRotaPatternCreate } from '../../hooks/useRotaPatternCreate'
import useUserSkills from '../../hooks/useUserSkills'
import { areSameWeek } from '../../utils.datetime'
import type { RotaPatternFormValues } from '../utils'
import InstanceFormCard from './InstanceFormCard'
import PatternFormCard from './PatternFormCard'
import ResidentFormCard from './ResidentFormCard'
import ShiftFormCard from './ShiftFormCard'

type NewRotaPatternModalProps = {
  testId?: string
  onCancel: () => void
  type: 'visit' | 'shift'
  initialValues?: InitialState
  residentId?: string
}

const NewRotaPatternModal: FC<NewRotaPatternModalProps> = ({
  testId,
  onCancel,
  type,
  initialValues = { reoccurenceTooglePattern: true, numberSupply: 1 },
  residentId
}) => {
  const { t } = useTranslation()
  const baseId = testId || `new-rota-pattern-${type}`
  const [form] = AntdForm.useForm()
  const [formData, setFormData] = useState<InitialState>(initialValues)
  const { createPattern, isLoadingCreate } = useRotaPatternCreate({
    type
  })
  const { createEvents } = useInstancesCreate()
  const { checkRotaGenerated } = useRotaGenerated()
  const { generateRota } = useGenerate({
    startDate:
      (formData.patternStart as string | undefined) || new Date().toISOString()
  })
  const { users } = useUsersList({ active: true })
  const { visitTypes } = useVisitTypesList()
  const { shiftTypes } = useShiftTypesList()
  const flags = useFeatureFlags({
    flagNames: [
      'rostering_editing_limitations',
      'new_create_pattern_save_and_duplicate',
      'rota_invoicing'
    ]
  })?.data
  const flagsRosteringEditingLimitations =
    flags?.rostering_editing_limitations?.enabled
  const flagsNewCreatePatternSaveAndDuplicate =
    flags?.new_create_pattern_save_and_duplicate?.enabled
  const flagRotaInvoicing = flags?.rota_invoicing?.enabled
  const home = useSelector(
    (state: { global: { home: HomeDto } }) => state.global.home
  )
  const userRole = useSelector(
    (state: { global: { user: Carer } }) => state.global.user?.role
  )
  const isAdmin =
    userRole === UserRole.careHomeAdmin || userRole === UserRole.management
  const { allSkills } = useUserSkills()
  const [isModalVisible, setIsModalVisible] = useState(true)
  const [duplicatedData, setDuplicatedData] = useState<InitialState | null>(
    null
  )
  const [isSubmitting, setIsSubmitting] = useState(false)
  const initialRender = useRef(true)

  // biome-ignore lint/correctness/useExhaustiveDependencies: <explanation>
  useEffect(() => {
    if (
      initialRender.current &&
      initialValues &&
      Object.keys(initialValues).length > 0
    ) {
      form.setFieldsValue(initialValues)
      setFormData((prev) => ({ ...prev, ...initialValues }))
      if (initialValues.startTime) {
        form.setFieldValue('startTime', initialValues.startTime)
      }
      if (initialValues.endTime) {
        form.setFieldValue('endTime', initialValues.endTime)
      }
      initialRender.current = false
    }
  }, [])

  const handleFinish = async (generateEvents: boolean) => {
    const patternFormValues: RotaPatternFormValues = {
      ...(formData as RotaPatternFormValues),
      patternEnd: dayjs.isDayjs(formData.patternEnd)
        ? formData.patternEnd.toISOString()
        : (formData.patternEnd as string | null | undefined)
    }

    const pattern = await createPattern(patternFormValues)

    const patternWithId = pattern as { _id?: string } | null | undefined
    if (patternWithId?._id) {
      if (generateEvents) {
        createEvents?.({
          patternId: patternWithId._id
        })
      }
      return true
    }

    return false
  }

  const onFinish = async (): Promise<boolean> => {
    const checkRotaGeneratedParams: GeneratedRota = {
      patternStartDate: formData.patternStart as string,
      patternEndDate: (formData.hasPatternEndDate as boolean)
        ? (formData.patternEnd as string | undefined)
        : undefined
    }

    const hasRota = await checkRotaGenerated(checkRotaGeneratedParams)
    const result = await handleFinish(Boolean(hasRota))

    if (result) {
      if (
        !hasRota &&
        formData.patternStart &&
        areSameWeek(formData.patternStart as string, new Date())
      ) {
        // The pattern itself is saved by this point, so a generation failure
        // shouldn't block the modal — but it must not pass silently either,
        // or the week looks empty with no explanation.
        generateRota({}).catch(() => {
          showToast({ title: t('SOMETHING_WENT_WRONG'), type: 'error' })
        })
      }
      setIsModalVisible(false)
      onCancel()
    }

    return result
  }

  const handleFormValuesChange = (values: InitialState) => {
    setFormData((prev) => ({ ...prev, ...values }))
  }

  const handleStartTimeChanged = (value?: string) => {
    if (formData.startTime !== value) {
      setFormData?.((prev) => ({ ...prev, startTime: value }))
    }
  }

  const handleEndTimeChanged = (value?: string) => {
    setFormData?.((prev) => ({ ...prev, endTime: value }))
  }

  const handleFrequencyValue = (value?: string) => {
    setFormData?.((prev) => ({ ...prev, frequency: value }))
  }

  const handleIntervalValue = (value?: number) => {
    setFormData?.((prev) => ({ ...prev, interval: value }))
  }

  const handleDaysValue = (value?: string[]) => {
    setFormData?.((prev) => ({ ...prev, specificDays: value }))
  }

  const handleSaveAndDuplicate = async () => {
    setIsSubmitting(true)
    try {
      await form.validateFields()

      const currentValues = form.getFieldsValue(true)

      // Check if events need to be generated
      const checkRotaGeneratedParams: GeneratedRota = {
        patternStartDate: formData.patternStart as string,
        patternEndDate: (formData.hasPatternEndDate as boolean)
          ? (formData.patternEnd as string | undefined)
          : undefined
      }

      const hasRota = await checkRotaGenerated(checkRotaGeneratedParams)
      const savedPattern = await handleFinish(Boolean(hasRota))

      if (savedPattern) {
        const duplicateData = {
          ...currentValues,
          title: `${currentValues.title} (duplicate)`,
          id: undefined,
          patternId: undefined
        }

        setDuplicatedData(duplicateData)

        setIsModalVisible(false)

        setTimeout(() => {
          setIsModalVisible(true)
        }, 100)
      }
    } catch (error) {
      console.error('Validation failed:', error)
    } finally {
      setIsSubmitting(false)
    }
  }

  const handleSave = async () => {
    setIsSubmitting(true)
    try {
      const success = await form
        .validateFields()
        .then(() => form.submit())
        .then(() => true)
        .catch(() => false)

      if (success) {
        return true
      }
      return false
    } catch (error) {
      console.error('Error saving pattern:', error)
      return false
    } finally {
      setIsSubmitting(false)
    }
  }

  // biome-ignore lint/correctness/useExhaustiveDependencies: <explanation>
  useEffect(() => {
    if (duplicatedData && isModalVisible) {
      form.setFieldsValue(duplicatedData)
      setFormData((prev) => ({ ...prev, ...duplicatedData }))
      setDuplicatedData(null)
    }
  }, [duplicatedData, isModalVisible, form])

  return (
    <>
      <Modal
        data-testid={baseId}
        open={isModalVisible}
        onCancel={() => {
          setIsModalVisible(false)
          onCancel()
        }}
        title={
          <StyledHeading level={3} data-testid={`${baseId}-title`}>
            {t('ADD_ROTA_PATTERN')}
          </StyledHeading>
        }
        width={648}
        footer={
          <FlexContainer data-testid={`${baseId}-footer`}>
            <ButtonsContainer>
              <Button
                key="cancel"
                data-testid={commonPatterns.button(baseId, 'cancel')}
                onClick={() => {
                  setIsModalVisible(false)
                  onCancel()
                }}
              >
                {t('CANCEL')}
              </Button>
              {flagsNewCreatePatternSaveAndDuplicate && (
                <Button
                  key="saveAndDuplicate"
                  data-testid={commonPatterns.button(baseId, 'save-duplicate')}
                  onClick={handleSaveAndDuplicate}
                >
                  {t('SAVE_AND_DUPLICATE')}
                </Button>
              )}
              <Button
                key="submit"
                variant="primary"
                data-testid={commonPatterns.button(baseId, 'save')}
                onClick={handleSave}
                isLoading={isLoadingCreate}
                data-cy="btn-save-modal"
              >
                {t('SAVE')}
              </Button>
            </ButtonsContainer>
          </FlexContainer>
        }
      >
        <StyledForm
          data-testid={`${baseId}-form`}
          form={form}
          onFinish={onFinish}
          onValuesChange={handleFormValuesChange}
          initialValues={formData}
        >
          {type === VISIT && (
            <>
              <ResidentFormCard
                testId={`${baseId}-resident-card`}
                form={form}
                home={home}
                setFormData={setFormData}
                onResidentChange={(selectedResidentId) => {
                  setFormData((prev) => ({
                    ...prev,
                    subjectId: selectedResidentId
                  }))
                }}
                formData={formData}
                isSubmitting={isSubmitting}
                prefilledResidentId={residentId}
              />
              <InstanceFormCard
                testId={`${baseId}-instance-card`}
                form={form}
                formData={formData}
                setFormData={setFormData}
                visitTypes={visitTypes}
                users={users}
                allSkills={allSkills}
                flagsRosteringEditingLimitations={
                  flagsRosteringEditingLimitations
                }
                flagRotaInvoicing={flagRotaInvoicing}
                isAdmin={isAdmin}
                isSubmitting={isSubmitting}
              />
            </>
          )}
          {type !== VISIT && (
            <ShiftFormCard
              testId={`${baseId}-shift-card`}
              shiftTypes={shiftTypes}
              users={users}
              formData={formData}
              form={form}
              setFormData={setFormData}
              isSubmitting={isSubmitting}
            />
          )}
          <PatternFormCard
            testId={`${baseId}-pattern-card`}
            form={form}
            formData={formData}
            setFormData={setFormData}
            onStartTimeChanged={handleStartTimeChanged}
            onEndTimeChanged={handleEndTimeChanged}
            onFrequencyValue={handleFrequencyValue}
            onIntervalValue={handleIntervalValue}
            onDaysValue={handleDaysValue}
          />
        </StyledForm>
      </Modal>
    </>
  )
}

const StyledForm = styled(Form)`
  display: flex;
  flex-direction: column;
  gap: 12px;
`

const StyledHeading = styled(Heading)`
  text-transform: none;
`

const FlexContainer = styled.div`
  display: flex;
  justify-content: flex-end;
  align-items: center;
`

export default NewRotaPatternModal
